"use client"

import React, { createContext, useContext, useReducer, useCallback, ReactNode, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, where, getDocs, Timestamp, getDoc, orderBy } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { useBudgetManager } from './BudgetManagerContext';

// Define types
export type InventoryItem = {
  id: string;
  name: string;
  quantity: number;
  price: number;
  category: string;
  budgetId: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
};

export type InventoryState = {
  items: InventoryItem[];
  loading: boolean;
  error: string | null;
};

type InventoryAction =
  | { type: 'LOAD_ITEMS'; payload: InventoryItem[] }
  | { type: 'ADD_ITEM'; payload: InventoryItem }
  | { type: 'UPDATE_ITEM'; payload: InventoryItem }
  | { type: 'DELETE_ITEM'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string };

type InventoryContextType = {
  state: InventoryState;
  loadInventoryItems: () => Promise<void>;
  addInventoryItem: (item: Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => Promise<void>;
  updateInventoryItem: (item: InventoryItem) => Promise<void>;
  deleteInventoryItem: (itemId: string) => Promise<void>;
};

const initialState: InventoryState = {
  items: [],
  loading: false,
  error: null,
};

const InventoryContext = createContext<InventoryContextType | undefined>(undefined);

function inventoryReducer(state: InventoryState, action: InventoryAction): InventoryState {
  switch (action.type) {
    case 'LOAD_ITEMS':
      return { ...state, items: action.payload, loading: false };
    case 'ADD_ITEM':
      return { ...state, items: [...state.items, action.payload], loading: false };
    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state.items.map(item => item.id === action.payload.id ? action.payload : item),
        loading: false,
      };
    case 'DELETE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload),
        loading: false,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}

export function InventoryProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(inventoryReducer, initialState);
  const { state: budgetState } = useBudgetManager();

  const loadInventoryItems = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');

      const q = query(
        collection(db, 'inventory_items'),
        where('budgetId', '==', currentBudget.id),
        where('createdBy', '==', user.uid),
        orderBy('createdAt', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const items: InventoryItem[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as Omit<InventoryItem, 'id'>;
        items.push({
          ...data,
          id: doc.id,
          createdAt: (data.createdAt as unknown as Timestamp).toDate(),
          updatedAt: (data.updatedAt as unknown as Timestamp).toDate(),
        });
      });

      dispatch({ type: 'LOAD_ITEMS', payload: items });
    } catch (error) {
      console.error('Error loading inventory items:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load inventory items' });
    }
  }, [budgetState.currentBudget]);

  const addInventoryItem = useCallback(async (item: Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      const currentBudget = budgetState.currentBudget;
      if (!currentBudget) throw new Error('No budget selected');

      const newItem = {
        ...item,
        budgetId: currentBudget.id,
        createdBy: user.uid,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, 'inventory_items'), newItem);
      const addedItem: InventoryItem = {
        ...newItem,
        id: docRef.id,
        createdAt: newItem.createdAt.toDate(),
        updatedAt: newItem.updatedAt.toDate(),
      };

      dispatch({ type: 'ADD_ITEM', payload: addedItem });
    } catch (error) {
      console.error('Error adding inventory item:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to add inventory item' });
    }
  }, [budgetState.currentBudget]);

  const updateInventoryItem = useCallback(async (item: InventoryItem) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      if (item.createdBy !== user.uid) {
        throw new Error('Not authorized to update this item');
      }

      const { id, ...updateData } = item;
      await updateDoc(doc(db, 'inventory_items', id), {
        ...updateData,
        updatedAt: Timestamp.now(),
      });

      dispatch({ type: 'UPDATE_ITEM', payload: { ...item, updatedAt: new Date() } });
    } catch (error) {
      console.error('Error updating inventory item:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update inventory item' });
    }
  }, []);

  const deleteInventoryItem = useCallback(async (itemId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      // Get the item first to check ownership
      const itemRef = doc(db, 'inventory_items', itemId);
      const itemSnapshot = await getDoc(itemRef);
      
      if (!itemSnapshot.exists()) {
        throw new Error('Item not found');
      }

      const itemData = itemSnapshot.data();
      if (itemData.createdBy !== user.uid) {
        throw new Error('Not authorized to delete this item');
      }

      await deleteDoc(itemRef);
      dispatch({ type: 'DELETE_ITEM', payload: itemId });
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete inventory item' });
    }
  }, []);

  useEffect(() => {
    if (budgetState.currentBudget) {
      loadInventoryItems();
    }
  }, [budgetState.currentBudget, loadInventoryItems]);

  return (
    <InventoryContext.Provider
      value={{
        state,
        loadInventoryItems,
        addInventoryItem,
        updateInventoryItem,
        deleteInventoryItem,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
}

export function useInventory(): InventoryContextType {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
}